import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconToggleLeft,
  IconToggleRight,
  IconSearch,
  IconFilter,
  IconBuilding,
  IconUsers,
} from "@tabler/icons-react";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import departmentServices from "../../../services/DepartmentServices";
import DatatablePaginated from "../../../jsx/components/DatatablePaginated";
import DepartmentModal from "./DepartmentModal";
import "./DepartmentList.css";
import "./DepartmentList.mobile.css";

const DepartmentList = () => {
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage] = useState(20);

  // Estados para filtros
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Carregar dados iniciais
  useEffect(() => {
    fetchDepartments(1, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Buscar departamentos com paginação
  const fetchDepartments = useCallback(
    async (page = 1, pageSize = 20, statusFilter = "all", search = "") => {
      setLoading(true);
      try {
        const params = {
          page: page || 1,
          page_size: pageSize || 20,
        };

        // Adicionar filtro de status
        const currentStatusFilter = statusFilter || filterStatus || "all";
        if (currentStatusFilter && currentStatusFilter !== "all") {
          params.is_active = currentStatusFilter === "active";
        }

        // Adicionar busca
        const currentSearch = search || searchTerm || "";
        if (currentSearch && currentSearch.trim() !== "") {
          params.search = currentSearch.trim();
        }

        const response = await departmentServices.fetchDepartments(params);

        setDepartments(response.results || []);
        setTotalRows(response.count || 0);
        setCurrentPage(response.current_page || 1);
      } catch (error) {
        console.error("Erro ao carregar departamentos:", error);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar os departamentos",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [filterStatus, searchTerm]
  );

  // Reagir às mudanças de filtros
  useEffect(() => {
    if (filterStatus !== undefined && searchTerm !== undefined) {
      setCurrentPage(1);
      fetchDepartments(1, perPage, filterStatus, searchTerm);
    }
  }, [filterStatus, searchTerm, perPage, fetchDepartments]);

  // Abrir modal para adicionar
  const handleAddNew = () => {
    setSelectedDepartment(null);
    setEditingId(null);
    setShowModal(true);
  };

  // Abrir modal para editar
  const handleEdit = useCallback((dept, e) => {
    e.stopPropagation();
    setSelectedDepartment(dept);
    setEditingId(dept.id);
    setShowModal(true);
  }, []);

  // Salvar departamento
  const handleSave = async (data) => {
    setLoading(true);
    try {
      await departmentServices.saveDepartment(data, editingId);

      Swal.fire({
        title: "Sucesso",
        text: editingId
          ? "Departamento atualizado com sucesso"
          : "Departamento criado com sucesso",
        icon: "success",
      });

      setShowModal(false);
      fetchDepartments(currentPage, perPage, filterStatus, searchTerm);
    } catch (error) {
      const errorMessage =
        error.response?.data?.name?.[0] ||
        error.response?.data?.detail ||
        "Erro ao salvar departamento";

      Swal.fire({
        title: "Erro",
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Remover departamento
  const handleDelete = useCallback(
    async (id, e) => {
      e.stopPropagation();
      const result = await Swal.fire({
        title: "Confirmar exclusão",
        text: "Tem certeza que deseja excluir este departamento? Os usuários vinculados terão o departamento removido.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
      });

      if (result.isConfirmed) {
        setLoading(true);
        try {
          await departmentServices.deleteDepartment(id);
          Swal.fire({
            title: "Sucesso",
            text: "Departamento removido com sucesso",
            icon: "success",
          });
          fetchDepartments(currentPage, perPage, filterStatus, searchTerm);
        } catch (error) {
          Swal.fire({
            title: "Erro",
            text: "Não foi possível remover o departamento",
            icon: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    },
    [currentPage, perPage, filterStatus, searchTerm, fetchDepartments]
  );

  // Alternar status ativo/inativo
  const toggleActive = useCallback(
    async (id, e) => {
      e.stopPropagation();
      setLoading(true);
      try {
        await departmentServices.toggleActiveStatus(id);
        fetchDepartments(currentPage, perPage, filterStatus, searchTerm);
      } catch (error) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível alterar o status",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [currentPage, perPage, filterStatus, searchTerm, fetchDepartments]
  );

  // Função para lidar com mudança de página
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchDepartments(page, perPage, filterStatus, searchTerm);
    },
    [perPage, filterStatus, searchTerm, fetchDepartments]
  );

  // Configuração para filtros de busca
  const filterByField = {
    fields: ["name", "description"],
    labels: ["Nome", "Descrição"],
  };

  const getStats = () => {
    const depts = departments || [];
    const total = depts.length;
    const active = depts.filter((d) => d && d.is_active).length;
    const inactive = total - active;
    const totalUsers = depts.reduce(
      (acc, d) => acc + (d?.users_count || 0),
      0
    );

    return { total, active, inactive, totalUsers };
  };

  const stats = getStats();

  // Definir as colunas da tabela
  const columns = useMemo(
    () => [
      {
        name: "Nome",
        selector: (row) => row.name,
        sortable: true,
        cell: (row) => (
          <div>
            <div style={{ fontWeight: "600", color: "var(--text-primary)" }}>
              {row.name}
            </div>
            {row.description && (
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                {row.description}
              </div>
            )}
          </div>
        ),
        grow: 2,
      },
      {
        name: "Usuários",
        selector: (row) => row.users_count,
        sortable: true,
        cell: (row) => (
          <div className="dept-users-count">
            <IconUsers size={14} />
            {row.users_count || 0}
          </div>
        ),
        width: "100px",
      },
      {
        name: "Criado por",
        selector: (row) => row.created_by_name,
        sortable: true,
        cell: (row) => (
          <span style={{ fontSize: "14px" }}>
            {row.created_by_name || <em>Sistema</em>}
          </span>
        ),
        width: "140px",
      },
      {
        name: "Criado em",
        selector: (row) => row.created_at,
        sortable: true,
        cell: (row) => (
          <span style={{ fontSize: "13px", color: "#6c757d" }}>
            {row.created_at
              ? formatDistance(new Date(row.created_at), new Date(), {
                  addSuffix: true,
                  locale: pt,
                })
              : "-"}
          </span>
        ),
        width: "140px",
      },
      {
        name: "Status",
        selector: (row) => row.is_active,
        sortable: true,
        cell: (row) => (
          <span
            className={`dept-status-badge ${row.is_active ? "active" : "inactive"}`}
          >
            {row.is_active ? "Ativo" : "Inativo"}
          </span>
        ),
        width: "100px",
      },
      {
        name: "Ações",
        cell: (row) => (
          <div
            style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}
          >
            <Button
              variant={row.is_active ? "warning" : "success"}
              size="sm"
              onClick={(e) => toggleActive(row.id, e)}
              title={row.is_active ? "Desativar" : "Ativar"}
              style={{ padding: "4px 6px" }}
            >
              {row.is_active ? (
                <IconToggleRight size={14} />
              ) : (
                <IconToggleLeft size={14} />
              )}
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={(e) => handleEdit(row, e)}
              title="Editar"
              style={{ padding: "4px 6px" }}
            >
              <IconEdit size={14} />
            </Button>

            <Button
              variant="danger"
              size="sm"
              onClick={(e) => handleDelete(row.id, e)}
              title="Excluir"
              style={{ padding: "4px 6px" }}
            >
              <IconTrash size={14} />
            </Button>
          </div>
        ),
        width: "140px",
        ignoreRowClick: true,
      },
    ],
    [toggleActive, handleEdit, handleDelete]
  );

  if (loading && departments.length === 0) {
    return (
      <div className="dept-container">
        <div className="dept-loading-container">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando departamentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dept-container">
      <div className="dept-header">
        <div className="dept-header-content">
          <h1 className="dept-title">
            <span className="dept-title-icon">
              <IconBuilding size={24} />
            </span>
            Departamentos
          </h1>
        </div>
      </div>

      <div className="dept-info-card">
        <p style={{ margin: 0 }}>
          Organize seus usuários por departamentos para facilitar a gestão e
          filtros. Cada usuário pode pertencer a um departamento.
        </p>
      </div>

      <div className="dept-search-container">
        <span className="dept-search-icon">
          <IconSearch size={18} />
        </span>
        <input
          type="text"
          className="dept-search-input"
          placeholder="Buscar departamentos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="dept-stats-container dept-stats-mobile">
        <div className="dept-stat-card">
          <div className="dept-stat-number">{stats.total}</div>
          <div className="dept-stat-label">Total</div>
        </div>
        <div className="dept-stat-card">
          <div className="dept-stat-number">{stats.active}</div>
          <div className="dept-stat-label">Ativos</div>
        </div>
        <div className="dept-stat-card">
          <div className="dept-stat-number">{stats.inactive}</div>
          <div className="dept-stat-label">Inativos</div>
        </div>
        <div className="dept-stat-card">
          <div className="dept-stat-number">{stats.totalUsers}</div>
          <div className="dept-stat-label">Usuários</div>
        </div>
      </div>

      {/* DataTable wrapper responsivo */}
      <div className="dept-mobile-container">
        <div className="dept-datatable-wrapper">
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Button
                variant={filterStatus === "all" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setFilterStatus("all")}
              >
                <IconFilter size={16} className="me-1" />
                Todos
              </Button>
              <Button
                variant={filterStatus === "active" ? "success" : "outline-success"}
                size="sm"
                onClick={() => setFilterStatus("active")}
              >
                Ativos
              </Button>
              <Button
                variant={
                  filterStatus === "inactive" ? "warning" : "outline-warning"
                }
                size="sm"
                onClick={() => setFilterStatus("inactive")}
              >
                Inativos
              </Button>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddNew}
              className="dept-add-button-desktop"
            >
              <IconPlus size={16} className="me-1" />
              Novo Departamento
            </Button>
          </div>

          {departments.length === 0 && !loading ? (
            <div className="dept-empty-state">
              <div className="dept-empty-icon">
                <IconBuilding size={48} />
              </div>
              <h3>
                {searchTerm
                  ? "Nenhum departamento encontrado"
                  : "Nenhum departamento configurado"}
              </h3>
              <p>
                {searchTerm
                  ? "Tente ajustar sua busca para encontrar departamentos."
                  : "Comece criando seus primeiros departamentos para organizar melhor sua equipe."}
              </p>
              {!searchTerm && (
                <Button variant="primary" onClick={handleAddNew}>
                  Criar Primeiro Departamento
                </Button>
              )}
            </div>
          ) : (
            <DatatablePaginated
              data={departments}
              columns={columns}
              totalRows={totalRows}
              isLoading={loading}
              handlePageChange={handlePageChange}
              filterByField={filterByField}
              filterDefaultField="name"
              searchBarType={1}
              classes="px-0"
            />
          )}
        </div>

        {/* Mobile Cards */}
        <div className="dept-mobile-cards">
          {loading ? (
            <div className="dept-mobile-loader">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando...</p>
            </div>
          ) : departments.length === 0 ? (
            <div className="dept-mobile-empty">
              <div className="dept-empty-icon">
                <IconBuilding size={48} />
              </div>
              <h3>
                {searchTerm
                  ? "Nenhum departamento encontrado"
                  : "Nenhum departamento configurado"}
              </h3>
              <p>
                {searchTerm
                  ? "Tente ajustar sua busca para encontrar departamentos."
                  : "Comece criando seus primeiros departamentos para organizar melhor sua equipe."}
              </p>
              {!searchTerm && (
                <Button variant="primary" onClick={handleAddNew}>
                  Criar Primeiro Departamento
                </Button>
              )}
            </div>
          ) : (
            <>
              {departments.map((dept) => (
                <div key={dept.id} className="dept-card">
                  {/* Header do card */}
                  <div className="dept-card-header">
                    <div className="dept-info">
                      <h4 className="dept-name">
                        <IconBuilding size={16} />
                        {dept.name}
                      </h4>
                      {dept.description && (
                        <p className="dept-description">{dept.description}</p>
                      )}
                    </div>
                    <span
                      className={`dept-card-status-badge ${dept.is_active ? "active" : "inactive"}`}
                    >
                      {dept.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </div>

                  {/* Body do card */}
                  <div className="dept-card-body">
                    <div className="dept-info-row">
                      <span className="dept-info-label">Usuários:</span>
                      <span className="dept-info-value">
                        <div className="dept-users-mobile">
                          <IconUsers size={14} />
                          {dept.users_count || 0}
                        </div>
                      </span>
                    </div>
                    <div className="dept-info-row">
                      <span className="dept-info-label">Criado por:</span>
                      <span className="dept-info-value">
                        {dept.created_by_name || "Sistema"}
                      </span>
                    </div>
                    <div className="dept-info-row">
                      <span className="dept-info-label">Criado em:</span>
                      <span className="dept-info-value dept-created-mobile">
                        {dept.created_at
                          ? formatDistance(new Date(dept.created_at), new Date(), {
                              addSuffix: true,
                              locale: pt,
                            })
                          : "-"}
                      </span>
                    </div>
                  </div>

                  {/* Actions do card */}
                  <div className="dept-card-actions">
                    <Button
                      size="sm"
                      variant={dept.is_active ? "warning" : "success"}
                      onClick={(e) => toggleActive(dept.id, e)}
                      className="dept-action-btn-mobile"
                      title={dept.is_active ? "Desativar" : "Ativar"}
                    >
                      {dept.is_active ? (
                        <IconToggleRight size={14} />
                      ) : (
                        <IconToggleLeft size={14} />
                      )}
                      {dept.is_active ? "Desativar" : "Ativar"}
                    </Button>

                    <Button
                      size="sm"
                      variant="primary"
                      onClick={(e) => handleEdit(dept, e)}
                      className="dept-action-btn-mobile"
                    >
                      <IconEdit size={14} />
                      Editar
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={(e) => handleDelete(dept.id, e)}
                      className="dept-action-btn-mobile"
                    >
                      <IconTrash size={14} />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}

              {/* Paginação mobile */}
              <div className="dept-mobile-pagination">
                <div className="dept-pagination-info">
                  Página {currentPage} de {Math.ceil(totalRows / perPage)}
                </div>
                <div className="dept-pagination-buttons">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="dept-pagination-btn"
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage >= Math.ceil(totalRows / perPage)}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="dept-pagination-btn"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <button
        className="dept-floating-button"
        onClick={handleAddNew}
        title="Novo Departamento"
      >
        <IconPlus size={24} />
      </button>

      {/* Modal de adição/edição */}
      <DepartmentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
        department={selectedDepartment}
        isEdit={!!editingId}
        loading={loading}
      />
    </div>
  );
};

export default DepartmentList;
